import { Config } from "@eagerdog/config";

let loadingPromise: Promise<void> | null = null;

/**
 * Loads the Google Maps JS SDK once (shared across the app), resolving
 * immediately if it's already loaded (e.g. by EventMapComponent).
 */
export const loadGoogleMapsScript = (): Promise<void> => {
  const w = window as any;

  if (w.google && w.google.maps) {
    return Promise.resolve();
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = new Promise<void>((resolve, reject) => {
    const apiKey = Config.web.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      loadingPromise = null;
      reject(new Error("Google Maps API key not configured"));
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      if (w.google && w.google.maps) {
        resolve();
        return;
      }
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () => {
        loadingPromise = null;
        reject(new Error("Failed to load Google Maps"));
      });
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadingPromise = null;
      reject(new Error("Failed to load Google Maps API."));
    };
    document.head.appendChild(script);
  });

  return loadingPromise;
};

export interface IDistanceResult {
  value: number; // in the requested unit (mi or km)
  unit: "mi" | "km";
}

/**
 * Calculates driving distance between two address strings using the Google
 * Distance Matrix service. Resolves undefined if the distance can't be
 * determined (e.g. an address can't be resolved).
 */
export const calculateDrivingDistance = async (
  originAddress: string,
  destinationAddress: string,
  unit: "mi" | "km"
): Promise<IDistanceResult | undefined> => {
  await loadGoogleMapsScript();

  const w = window as any;
  if (!w.google?.maps?.DistanceMatrixService) {
    return undefined;
  }

  const service = new w.google.maps.DistanceMatrixService();

  const response: any = await new Promise((resolve, reject) => {
    service.getDistanceMatrix(
      {
        origins: [originAddress],
        destinations: [destinationAddress],
        travelMode: "DRIVING",
        unitSystem: unit === "mi" ? w.google.maps.UnitSystem.IMPERIAL : w.google.maps.UnitSystem.METRIC,
      },
      (result: any, status: any) => {
        if (status === "OK") {
          resolve(result);
        } else {
          reject(new Error(`Distance Matrix request failed: ${status}`));
        }
      }
    );
  });

  const element = response?.rows?.[0]?.elements?.[0];
  if (!element || element.status !== "OK") {
    return undefined;
  }

  const meters = element.distance.value;
  const value = unit === "mi" ? meters / 1609.34 : meters / 1000;

  return { value: Math.round(value * 10) / 10, unit };
};

export interface ILatLng {
  lat: number;
  lng: number;
}

/**
 * Geocodes a free-text address string into coordinates using the Google
 * Geocoder. Resolves undefined if the address can't be resolved.
 */
export const geocodeAddress = async (address: string): Promise<ILatLng | undefined> => {
  if (!address) return undefined;

  await loadGoogleMapsScript();

  const w = window as any;
  if (!w.google?.maps?.Geocoder) {
    return undefined;
  }

  const geocoder = new w.google.maps.Geocoder();

  try {
    const result: any = await new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results: any, status: any) => {
        if (status === "OK" && results?.[0]) {
          resolve(results[0]);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });

    return {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
    };
  } catch (e) {
    console.warn("Could not geocode address:", e);
    return undefined;
  }
};
